import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { InvoiceDocument } from '../components/InvoiceDocument';
import { InvoicePriceSource, Order, Product } from '../types';
import { getDefaultShipFromAddress } from '../services/shipFromAddressService';
import { fetchProductById, searchProducts } from '../services/productService';

interface GenerateInvoiceOptions {
  order: Order;
  invoiceNumber?: string;
  awbNumber?: string;
  productData?: EnhancedProductData;
  convertedPrices?: number[];
  priceSource?: InvoicePriceSource;
}

export interface EnhancedProductData {
  [itemId: string]: Product;
}

export const generateInvoiceNumber = (order: Order): string => {
  const date = new Date(order.createdAt);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const orderNum = order.orderNumber.replace('#', '');
  return `INV-${year}-${month}-${orderNum}`;
};

export const getAWBNumber = (order: Order): string => {
  if (order.easyPostLabel?.trackingCode) {
    return order.easyPostLabel.trackingCode;
  }
  if (order.tracking?.trackingNumber) {
    return order.tracking.trackingNumber;
  }
  if (order.orderTracking?.trackingNumber) {
    return order.orderTracking.trackingNumber;
  }
  return 'N/A';
};

const loadProductData = async (order: Order): Promise<EnhancedProductData> => {
  const enhancedData: EnhancedProductData = {};
  
  try {
    // Fetch product data for each order item
    for (const item of order.items) {
      try {
        // First try to fetch by product ID
        let product = await fetchProductById(item.id);
        
        // If not found by ID, search by name (exact match)
        if (!product) {
          const searchResults = await searchProducts(item.name.trim());
          product = searchResults.products.find(p => p.name.trim().toLowerCase() === item.name.trim().toLowerCase()) || null;
        }
        
        if (product) {
          enhancedData[item.id] = product;
        }
      } catch (error) {
        console.warn(`Could not fetch product data for item ${item.id} (${item.name}):`, error);
        // Continue with other items even if one fails
      }
    }
  } catch (error) {
    console.error('Error loading product data:', error);
    // Return what we have so far
  }
  
  return enhancedData;
};

export const generateAndDownloadInvoice = async ({
  order,
  invoiceNumber,
  awbNumber,
  productData: providedProductData,
  convertedPrices,
  priceSource = 'order',
}: GenerateInvoiceOptions): Promise<void> => {
  try {
    const shipFromAddress = await getDefaultShipFromAddress();
    
    if (!shipFromAddress) {
      throw new Error('No default ship-from address found. Please configure in settings.');
    }

    const shipperInfo = {
      name: shipFromAddress.customsSigner || shipFromAddress.name,
      company: shipFromAddress.company,
      address: shipFromAddress.line1 + (shipFromAddress.line2 ? `, ${shipFromAddress.line2}` : ''),
      city: shipFromAddress.city,
      state: shipFromAddress.state,
      zipCode: shipFromAddress.zipCode,
      country: shipFromAddress.country,
      phone: shipFromAddress.phone,
      email: shipFromAddress.email,
    };

    const finalInvoiceNumber = invoiceNumber || generateInvoiceNumber(order);
    const finalAwbNumber = awbNumber || getAWBNumber(order);
    
    // Use provided product data or load it if not provided
    const productData = providedProductData || await loadProductData(order);

    const element = React.createElement(InvoiceDocument, {
      order,
      shipperInfo,
      invoiceNumber: finalInvoiceNumber,
      awbNumber: finalAwbNumber,
      productData,
      convertedPrices,
      priceSource,
    }) as unknown as React.ReactElement<import('@react-pdf/renderer').DocumentProps>;
    const blob = await pdf(element).toBlob();

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Invoice-${order.orderNumber}-${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating invoice:', error);
    throw error;
  }
};
